import { Component, ViewChild, Inject, ElementRef, Renderer2, ViewChildren, QueryList, DOCUMENT, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription, firstValueFrom, timer, zip } from "rxjs";
import { DateTime } from "luxon";
import { environment } from "../../environments/environment";
import { LegendPosition } from "@swimlane/ngx-charts";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { Pair } from "../obj/Pair";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { DatePipe, NgStyle } from "@angular/common";
import { CurrencyPipe } from "@angular/common";
import { DecimalPipe } from "@angular/common";
import { Account } from "../entity/Account";
import { SellOffer } from "../entity/SellOffer";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { LoaderService } from "../service/loader.service";
import { User } from "../entity/User";
import { Contract } from "../entity/Contract";
import { DiffBenchmark } from "../obj/DiffBenchmark";
import { OffersData } from "../obj/OffersData";
import { ConsumptionDataType } from "../obj/ConsumptionDataType";
import { RateData } from "../obj/RateData";
import { Serie } from "../obj/Serie";
import { CurrentContractData } from "../obj/CurrentContractData";
import { SlidesOutputData, CarouselModule, OwlOptions } from "ngx-owl-carousel-o";
import { IConsumptionDialog } from "./incs/i-dialog-consumption.component";
import { AITask } from "../entity/AITask";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ValidationService } from "../service/validation.service";
import { State } from "../entity/State";
import { AccountSelection } from "../obj/AccountSelection";
import { SavingsInfo } from "../obj/SavingsInfo";
import { AccountData } from "../obj/AccountData";
import { Company } from "../entity/Company";
import { HeaderComponent } from "./incs/header.component";
import { UserCurrentCostDataComponent } from "./incs/user-current-cost-data.component";
import { UserHomeAISubscriptionComponent } from "./incs/user-home-ai-subscription.component";
import { KilowantBarHomeOneOfferChartComponent } from "./chart/kilowant-charts-home-one-offer.component";
import { UserHomePriceStatisticsComponent } from "./incs/user-home-price-statistics.component";
import { UserHomeDetailsComponent } from "./incs/user-home-details.component";
import { AccountEditComponent } from "./incs/account-edit.component";
import { NgxMaskDirective } from "ngx-mask";
import { OneClickDirective } from "./helpers/one-click.component";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { TotalSavingsInfo } from "../obj/TotalSavingsInfo";
import { MatTabNav, MatTabLink } from "@angular/material/tabs";

@Component({
    selector: "kilowant-user-home",
    templateUrl: "../../html/main-user-home.html",
    imports: [HeaderComponent, FormsModule, CarouselModule, UserCurrentCostDataComponent,
        UserHomeAISubscriptionComponent, NgStyle, KilowantBarHomeOneOfferChartComponent, UserHomePriceStatisticsComponent,
        UserHomeDetailsComponent, AccountEditComponent, NgxMaskDirective, ReactiveFormsModule, OneClickDirective,
        CurrencyPipe, DatePipe, MatTabNav, MatTabLink]
})
export class UserHomeComponent extends AbstractBuyerComponent {

    @ViewChild("body") bodyElement: ElementRef;
    @ViewChild("currentPlanBody") currentPlanBodyElement: ElementRef;
    @ViewChild("step1") step1Element: ElementRef;
    @ViewChild("step2") step2Element: ElementRef;
    @ViewChild("step3") step3Element: ElementRef;
    @ViewChildren("area") areas: QueryList<ElementRef>;
    @ViewChild('contractsSlider', { static: false }) contractsSlider: any;

    selectSort: MatSort;
    @ViewChild("matSortSelectedOffers") set matSortSelectedOffers(sort: MatSort) {
        this.selectSort = sort;
    }

    callUpdate: Subject<boolean> = new Subject();
    callCancel: Subject<boolean> = new Subject();

    RIColor: string = "#" + environment.RI_UTILITY_COLOR;
    RIEstimatedColor: string = "#" + environment.RI_UTILITY_FORECASTED_COLOR;
    CommunityColor: string = "#" + environment.RI_COMMUNITY_COLOR;
    CommunityEstimatedColor: string = "#" + environment.RI_COMMUNITY_FORECASTED_COLOR;

    howItWorksOpened = signal<boolean>(false);
    howPricePlansQualifyOpened = signal<boolean>(false);

    sliderOptions: OwlOptions = {
        loop: false,
        nav: false,
        items: 1,
        dots: false,
        navText: ["", ""],
        autoplay: false,
        smartSpeed: 1000,
        responsive: {
            0: {
                items: 1
            },
            600: {
                items: 2
            },
            1000: {
                items: 1
            }
        },
    }
    sliderMaxElements: number = 4;

    // Signals
    account = signal<Account | null>(null);
    currentCost = signal<CurrentContractData[]>([]);
    selectSupplierMode = signal<boolean>(false);
    setStartingContract = signal<number>(9);
    selectModeChanged = signal<boolean>(false);
    offersLoadingMode = signal<boolean>(true);
    sliderPositions = signal<number[]>([]);
    sliderCurrentPosition = signal<number>(0);
    max_savings = signal<string>('');
    max_savings_date = signal<Date | null>(null);
    aiSubsctiptionMode = signal<boolean>(false);
    nooffers_slide_switch_1 = signal<boolean>(true);
    nextContractLevel = signal<number>(0);
    activeAITask = signal<AITask | null>(null);
    startAnimation = signal<number>(0);
    step = signal<number>(0);
    interactiveStep = signal<number>(0);
    viewOfferDetails = signal<boolean>(false);
    numberOffersLoad = signal<number>(-1);
    offerViewFilter = signal<string>('trending');
    additionalOffersTermsFilter = signal<string | null>(null);
    additionalOffersUsedTerms = signal<Pair[]>([]);
    additionalOffersSavingsFilter = signal<string | null>(null);
    additionalOffersUsedSavings = signal<Pair[]>([]);
    offers = signal<OffersData[]>([]);
    viewSmallChart = signal<any[]>([]);
    offersForChart = signal<any[]>([]);
    savings = signal<number[]>([]);
    savingsInUSD = signal<number[]>([]);
    showSavingsIn = signal<string>('USD');
    hasAdditionalOffers = signal<boolean>(false);
    additionalOffersPage = signal<number>(1);
    additionalOffersCount = signal<number>(0);
    client = signal<User | null>(null);
    introHide = signal<boolean>(false);
    viewAccounts = signal<boolean>(false);
    showDisclosureStatement = signal<boolean>(false);
    aiEditMode = signal<boolean>(false);
    selectedOffer = signal<OffersData | null>(null);
    sellOfferContact = signal<User | null>(null);
    selectedOfferPrice = signal<number>(0);
    diffBenchmark = signal<DiffBenchmark>(DiffBenchmark.UTILITY);
    benchmark = signal<string>('');
    showSellerContractTerms = signal<boolean>(false);
    showNSAISettings = signal<boolean>(false);
    aiSubscription = signal<boolean>(true);
    currentOffersNumber = signal<number>(0);
    currentOffersNumberStep = signal<number>(0);
    selectAllChecked = signal<boolean>(false);
    accountsDataConfirmNeeded = signal<boolean>(true);
    currentAccountStep = signal<number>(-1);
    password = signal<string>('');
    password2 = signal<string>('');
    passwordType = signal<string>('password');
    passwordType2 = signal<string>('password');
    companyName = signal<string | null>(null);
    allStates = signal<State[]>([]);
    cities = signal<string[]>([]);
    selectedOffersPeriod = signal<number>(0);
    isPreregisterMode = signal<boolean>(false);
    userSavings = signal<TotalSavingsInfo | null>(null);

    offerDetailsRestart: Subject<OffersData> = new Subject();

    // Non-signal properties
    selectedAccountsIds: number[] = [];
    selectedAccounts: (Account | null)[] = [];
    accountsSelector: AccountSelection[] = [];

    iConsumptionDialogRef: MatDialogRef<IConsumptionDialog> = null!;
    totalOffers: number;
    scrollObservation: Subscription;
    document: Document;
    savingPresent: boolean = false;
    savingsInUSDPresent: boolean;
    max_savings_rate: number | null = null;
    paying_diff: number | null = null;
    diff_start: string | null = null;
    selectionOffer: boolean = false;
    viewAllOffers: boolean = false;
    latestUtilityDate: string = "";
    latestCommunityDate: string = "";
    utilityAverageForHistory: number = 0;
    communityAverageForHistory: number = 0;
    termFrom: number | null;
    termTo: number | null;
    view: [number, number] = [400, 250];
    legend: boolean = true;
    legendTitle: string = "";
    legendPosition: LegendPosition = LegendPosition.Below;
    showLabels: boolean = true;
    animations: boolean = true;
    xAxis: boolean = true;
    yAxis: boolean = false;
    showYAxisLabel: boolean = false;
    showXAxisLabel: boolean = false;
    xAxisLabel: string = "Year";
    yAxisLabel: string = "";
    colorScheme = { domain: ["#D0D0D0", "#A0A0A0"], name: "kilowant", selectable: true, group: null! };
    viewOffersType: string = "default";
    viewSelectedOffersType: string | null = null;
    viewAccountsType: string = "all";
    historyContractVals: number[] = [];
    historyContractShiftsLeft: number[] = [];
    historyContractShiftsRight: number[] = [];
    scrollNeeded: boolean = false;
    selectSortInitialised: boolean = false;
    selectOffersSource: MatTableDataSource<any>;
    selectedOfferPosition: number | null = null;
    additionalOffersAllTerms: Pair[] = [new Pair(1, 9), new Pair(10, 18), new Pair(19, 36)];
    additionalOffersAllSavings: Pair[] = [new Pair(16, 999), new Pair(6, 15), new Pair(1, 5)];
    selectedAccountsChanged: boolean[] = [];
    fileToUpload: File | null = null;
    hasFullConsumption: boolean = false;
    viewPriceHistory: boolean = false;
    priceHistoryLoaded: boolean = false;
    firstLoadPriceHistory: boolean = false;
    future_price: number = 0;
    future_months: number = 0;
    rates: RateData[] = [];
    pagedRates: RateData[] = [];
    chartLineName: string = "";
    selectedDatasets: number[] = [];
    startRates: number = 0;
    ratesPerPage: number = 12;
    historicalContracts: Contract[];
    changed: boolean = false;
    currentPhoneNumber: FormControl = new FormControl("");
    states: State[] = [];
    currentAccountNumber: FormControl = new FormControl("");
    forceCloseSubject: Subject<void> = new Subject<void>();
    forceOpenSubject: Subject<void> = new Subject<void>();
    introHideLink: any = null;
    autoOpenContractId: number = 0;
    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    timerOpenAI: any = null;
    autoOpenPriceHistory = signal<boolean>(false);
    initOpenDivsFinished: boolean = false;
    switchAccountDelay: any = null;
    timerOpenSelectedPlan: any = null;

    // The drawer opens DRAWER_OPEN_DELAY after the offers resolve — this method is
    // called at that moment from both load paths, so the delay is measured from
    // there and from nothing else. An account already running a Nonstop Shop AI
    // task keeps the 1s it has always had: the drawer is that task's own status,
    // not a prompt to act on.
    static readonly DRAWER_OPEN_DELAY = 5000;
    static readonly DRAWER_OPEN_DELAY_AI_TASK = 1000;
    static readonly SELECTED_PLAN_OPEN_DELAY = 8000;


    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        protected numberpipe: DecimalPipe,
        private renderer: Renderer2,
        protected loaderService: LoaderService,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        @Inject(DOCUMENT) document: Document
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
        this.document = document;
    }

    ngOnInit(): void {
        this.loaderService.setDisabled(true);
        this.initPromises.push(this.generalService.getStates(true)
            .then(states => this.allStates.set(states)));
        this.initPromises.push(firstValueFrom(this.generalService.totalOffers()).then((x: any) => this.totalOffers = x));

        this.route.fragment.subscribe((fragment: string | null) => {
            if (fragment != null && fragment?.startsWith('contract-')) {
                this.autoOpenContractId = parseInt(fragment.substring(9));
            }
        });

        super.ngOnInit();
    }

    ngOnChanges(): void {
        this.loaderService.setDisabled(true);
    }

    pageStart(): void {
        this.loaderService.setDisabled(true);
        this.checkIfClientSelected(null);
        this.eventSubscriptions.push(this.generalService.changeAccountActivities$.subscribe(
            account => {
                if (account!=null && account != this.account()) {
                    this.changeAccount(account);
                    this.loadSavings();
                }
            })
        );
        this.eventSubscriptions.push(this.generalService.changeClientActivities$.subscribe(
            client => {
                this.checkIfClientSelected(client);
            })
        );
        if (this.user.isPreRegister) {
            this.isPreregisterMode.set(true);
        } else {
            this.isPreregisterMode.set(false);
        }
    }

    ngAfterViewInit(): void {
    }

    nextAnimationStep(): void {
        this.startAnimation.update(val => val + 1);
        if (this.startAnimation() < 3) {
            var t = this;
            setTimeout(() => { t.nextAnimationStep(); }, 500);
        } else {
            var t = this;
            this.introHideLink = setTimeout(() => { t.introHide.set(true); }, (this.aiSubsctiptionMode()) ? 6000 : 3000);
        }
    }

    nextTotalOffersStep(): void {
        if (this.currentOffersNumberStep() == 0) {
            this.currentOffersNumberStep.set(Math.max(1, Math.floor(this.totalOffers / 50)));
        }
        this.currentOffersNumber.update(val => val + this.currentOffersNumberStep());
        if (this.currentOffersNumber() < this.totalOffers) {
            var t = this;
            setTimeout(() => { t.nextTotalOffersStep(); }, 30);
        }
    }

    ngOnDestroy() {
        this.cancelDrawerTimer();
        this.cancelSelectedPricePlanTimer();
        this.renderer.removeClass(document.body, "light-grey");
        this.loaderService.setDisabled(false);
        super.ngOnDestroy();
    }

    changeAccount(account: Account | null) {
        if (this.switchAccountDelay != null) {
            clearTimeout(this.switchAccountDelay);
        }
        this.cancelDrawerTimer();
        this.cancelSelectedPricePlanTimer();
        this.autoOpenPriceHistory.set(false);
        this.step.set(0);
        this.initOpenDivsFinished = false;
        setTimeout(() => { this.interactiveStep.set(0); }, 1);
        this.viewPriceHistory = false;
        this.nextContractLevel.set(0);
        this.priceHistoryLoaded = false;
        this.firstLoadPriceHistory = false;
        this.currentCost.set([]);
        this.startAnimation.set(0);
        this.aiSubsctiptionMode.set(false);
        this.aiEditMode.set(false);
        this.nooffers_slide_switch_1.set(true);
        this.activeAITask.set(null);
        this.offersLoadingMode.set(true);
        this.introHide.set(false);
        this.currentOffersNumber.set(0);
        this.currentOffersNumberStep.set(0);
        if (this.introHideLink != null) {
            clearTimeout(this.introHideLink);
        }

        this.account.set(account);
        this.max_savings.set('');
        if (this.account() != null) {
            this.diffBenchmark.set(DiffBenchmark.UTILITY);
            if (this.account()?.isCommunity) {
                this.diffBenchmark.set(DiffBenchmark.COMMUNITY);
            }
            this.showSavingsIn.set("USD");
            this.selectedOffer.set(null);
            this.sellOfferContact.set(null);
            this.selectedOfferPosition = null;
            this.generalService.getAccountCurrentCosts(this.account()!.id).then((data: any) => {
                this.currentCost.set(data);
                this.selectModeChanged.set(false);
                this.selectSupplierMode.set(false);
                this.selectCurrentCosts();
                this.numberOffersLoad.set(-1);
                this.aiSubsctiptionMode.set(false);
                this.loadEstimatedDates();
                this.selectSortInitialised = false;
                this.offerViewFilter.set('trending');
                this.viewSelectedOffersType = null;
                this.hasFullConsumption = (this.currentCost()[this.selectedOffersPeriod()].consumptionData == ConsumptionDataType.FULL);
                this.openConsumptionIfNotFilledDuringPageLoad();
                this.loadOffers(false, false);
                this.selectOffersSource = new MatTableDataSource();
                setTimeout(() => { this.startStepsAnimation(); }, 2000);
                this.nextTotalOffersStep();

                if (this.autoOpenContractId > 0) {
                    for (let i = 0; i < this.currentCost().length; i++) {
                        if (!this.currentCost()[i].isRate && this.currentCost()[i].contractId == this.autoOpenContractId) {
                            setTimeout(() => { this.contractSliderChangeSliderPosition(i); }, 100);
                            this.timerOpenSelectedPlan = setTimeout(() => { this.forceOpenSubject.next(); }, UserHomeComponent.SELECTED_PLAN_OPEN_DELAY);
                            break;
                        }
                    }
                }

            });
        }
    }

    switchToSelectMode(): void {
        this.selectModeChanged.set(false);
        this.setStartingContract.set(9);
        this.selectSupplierMode.set(true);
    }

    switchToNormalMode(): void {
        this.selectSupplierMode.set(false);
    }

    switchCurrentCostDataState(ifOpened: boolean): void {
        for (let i = 0; i < this.currentCost().length; i++) {
            if (i != this.sliderCurrentPosition()) {
                if (ifOpened) {
                    this.forceOpenSubject.next();
                } else {
                    this.forceCloseSubject.next();
                }
            }
        }
    }

    selectCurrentCosts(): void {
        this.selectedOffersPeriod.set(-1);
        for (let i = 0; i < this.currentCost().length; i++) {
            if (this.currentCost()[i].isRate && this.selectedOffersPeriod() == -1) {
                this.selectedOffersPeriod.set(i);
            }
        }
        var aiTaskId = this.getActiveAITaskID();
        if (aiTaskId != null) {
            this.loadAITask(aiTaskId);
        }
        this.refreshContractSliderPositions();
    }

    getActiveAITaskID(): number | null {
        if (this.currentCost() == null || this.currentCost().length == 0) {
            return null;
        }
        for (var i = 0; i < this.currentCost().length; i++) {
            if (this.currentCost()[i].aiTaskId != null && this.currentCost()[i].aiTaskId!>0) {
                return this.currentCost()[i].aiTaskId;
            }
        }
        return null;
    }

    gapPeriodLargeEnough(cCost: CurrentContractData): boolean {
        if (cCost.termTo == null) {
            return true;
        }
        return (this.getTermPeriod(cCost.termFrom, cCost.termTo) + 1 >= this.generalService.MINIMAL_GAP_PERIOD);
    }

    startStepsAnimation(): void {
        this.offersLoadingMode.set(false);
        var t = this;
        setTimeout(() => { t.nextAnimationStep(); }, 100);
        setTimeout(() => { t.gotoStep(1); }, 100);
    }

    openConsumptionDialog(isForm: boolean, isAIBased: boolean, account: Account, resFunc: Function | null): void {
        this.iConsumptionDialogRef = this.dialog.open(IConsumptionDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.iConsumptionDialogRef.afterClosed().subscribe(() => {
            this.iConsumptionDialogRef = null!;
        });
        this.iConsumptionDialogRef.componentInstance.updateConsumption.subscribe((rewriteEstimationMode: boolean) => {
            this.iConsumptionDialogRef = null!;
            if (resFunc != null) {
                resFunc();
            } else {
                this.changeAccount(this.account());
            }
            if (rewriteEstimationMode && !this.user.isPreRegister) {
                this.openBottomSlide();
            }
        });
        this.iConsumptionDialogRef.componentInstance.isForm = isForm;
        this.iConsumptionDialogRef.componentInstance.isAIBased = isAIBased;
        this.iConsumptionDialogRef.componentInstance.account = account;
    }

    openNonstopShoppingInfo(): void {
        this.iIconPopup('<h2>Nonstop Savings Subscription</h2><p>kilowant\'s AI enabled Nonstop Shop* feature protects you against missing out on future savings opportunities, by searching and sourcing future price plan offers during and after the term of your current contract. Our AI also eliminates unauthorized renewals at the end of your contract term.</p><p></p><p>Here\'s how it works:</p><p></p><p>1. Only price plan offers with savings vs. your alternative utility or community price plan will qualify for Nonstop Shop.<br/><br/>2. On your behalf Nonstop Shop will automatically accept the lowest priced qualifying offer for the accounts included in your price plan contract [listed in Step 2 of 3], triggered by the parameters of your selected AI Level.<br/><br/>3. As soon as Nonstop Shop AI accepts an offer, you will be notified of the offer terms via email with the option to cancel the contract within the designated rescission period.  Your future price plans will periodically change in accordance with the parameters of your selected Nonstop Shop option.<br/></p><p></p><p>* You can modify or deactivate your subscription at any time by going to Main Menu > AI Tasks</p>');
    }

    loadEstimatedDates(): void {
        this.generalService.getAccountGetLatestUtilityDate(this.account()!.id).then(x => {
            if (x.key != null) {
                this.latestUtilityDate = this.datepipe.transform(x.key, "MMM. y")!;
            }
            if (x.value != null) {
                this.latestCommunityDate = this.datepipe.transform(x.value, "MMM. y")!;
            }
        });
    }

    loadOffers(isPrev: boolean, isNext: boolean): void {
        if (isPrev) {
            this.additionalOffersPage.update(val => val - 1);
            if (this.additionalOffersPage() == 0) {
                this.additionalOffersPage.set(1);
                return;
            }
        }
        if (isNext) {
            this.additionalOffersPage.update(val => val + 1);
            if ((this.additionalOffersPage() - 1) * 3 >= this.additionalOffersCount()) {
                this.additionalOffersPage.update(val => val - 1);
                return;
            }
        }
        if (this.account() != null) {
            if (this.currentCost()[this.selectedOffersPeriod()] == null) {
                this.router.navigate(["/user/home"]);
                return;
            }
            if (!this.hasOffersWithSavings()) {
                this.numberOffersLoad.set(0);
                if (this.offerViewFilter() != 'additional') {
                    this.aiSubsctiptionMode.set(true);
                    var t = this;
                    setTimeout(() => { t.nooffers_slide_switch_1.set(false); }, 6000);
                }
                this.initOpenDivs();
                return;
            }
            if (this.offerViewFilter() == 'trending') {
                this.generalService.getTopSellOfferPerAccount(this.account()!, null, null).then((data: any) => {
                    this.hasAdditionalOffers.set(data.key);
                    this.offers.set(data.value);
                    this.afterLoadOffers();
                });
            } else {
                let periodFrom = this.currentCost()[this.selectedOffersPeriod()].termFrom;
                this.generalService.getOffersListPerAccount(this.account()!,
                    periodFrom, null, (this.additionalOffersTermsFilter() == null) ? null : this.getFromListSelectedVariant(this.additionalOffersTermsFilter()!, this.additionalOffersUsedTerms()).key, (this.additionalOffersTermsFilter() == null) ? null : this.getFromListSelectedVariant(this.additionalOffersTermsFilter()!, this.additionalOffersUsedTerms()).value - 1,
                    (this.additionalOffersSavingsFilter() == null) ? null : this.getFromListSelectedVariant(this.additionalOffersSavingsFilter()!, this.additionalOffersUsedSavings()).key, (this.additionalOffersSavingsFilter() == null) ? null : this.getFromListSelectedVariant(this.additionalOffersSavingsFilter()!, this.additionalOffersUsedSavings()).value - 1,
                    this.additionalOffersPage(),
                    "TermFrom", false).subscribe((data: any) => {
                        this.additionalOffersCount.set(data.cnt);
                        let usedPositions = [];
                        const newUsedTerms = [];
                        for (let i = 0; i < data.listOfTerms.length; i++) {
                            for (let y = 0; y < this.additionalOffersAllTerms.length; y++) {
                                if (this.additionalOffersAllTerms[y].key <= data.listOfTerms[i] && this.additionalOffersAllTerms[y].value > data.listOfTerms[i] && usedPositions.indexOf(y) == -1) {
                                    usedPositions.push(y);
                                    newUsedTerms.push(this.additionalOffersAllTerms[y]);
                                }
                            }
                        }
                        this.additionalOffersUsedTerms.set(newUsedTerms);
                        usedPositions = [];
                        const newUsedSavings = [];
                        for (let i = 0; i < data.listOfSavings.length; i++) {
                            for (let y = 0; y < this.additionalOffersAllSavings.length; y++) {
                                if (this.additionalOffersAllSavings[y].key <= data.listOfSavings[i] && this.additionalOffersAllSavings[y].value > data.listOfSavings[i] && usedPositions.indexOf(y) == -1) {
                                    usedPositions.push(y);
                                    newUsedSavings.push(this.additionalOffersAllSavings[y]);
                                }
                            }
                        }
                        this.additionalOffersUsedSavings.set(newUsedSavings);
                        this.offers.set(data.data);
                        this.afterLoadOffers();
                    });
            }
        }
    }

    armDrawerTimer(delay: number): void {
        this.cancelDrawerTimer();
        this.timerOpenAI = setTimeout(() => { this.timerOpenAI = null; this.openBottomSlide(); }, delay);
    }

    cancelDrawerTimer(): void {
        if (this.timerOpenAI != null) {
            clearTimeout(this.timerOpenAI);
            this.timerOpenAI = null;
        }
    }

    cancelSelectedPricePlanTimer(): void {
        if (this.timerOpenSelectedPlan != null) {
            clearTimeout(this.timerOpenSelectedPlan);
            this.timerOpenSelectedPlan = null;
        }
    }

    initOpenDivs(): void {
        if (this.initOpenDivsFinished) {
            return;
        }
        if (this.numberOffersLoad() == 0) {
            this.armDrawerTimer(UserHomeComponent.DRAWER_OPEN_DELAY);
            // Ask for the price plan to open; each panel decides WHEN, as soon as its
            // own history has loaded (user-current-cost-data.component.ts). This used
            // to be a 9s timer, which was really "long enough for a second HTTP call
            // nobody was watching" — on a slow connection it opened around an empty
            // chart, and after the drawer moved to 5s it would have opened after it.
            this.autoOpenPriceHistory.set(true);
        } else {
            if (this.getActiveAITaskID() && this.getPriceStatsText(this.offers()[0]) == "low") {
                // in case of low prices do not open bottom slider
            } else {
                this.armDrawerTimer((this.getActiveAITaskID()) ? UserHomeComponent.DRAWER_OPEN_DELAY_AI_TASK : UserHomeComponent.DRAWER_OPEN_DELAY);
            }
        }
        this.initOpenDivsFinished = true;
    }

    hasOffersWithSavings(): boolean {
        for (let i = 0; i < this.currentCost().length; i++) {
            if (this.currentCost()[i].isRate && this.currentCost()[i].hasOffersWithSavings) {
                return true;
            }
        }
        return false;
    }

    getFromListSelectedVariant(key: string, data: Pair[]): Pair {
        for (let i = 0; i < data.length; i++) {
            if (data[i].key + '-' + data[i].value == key) {
                return data[i];
            }
        }
        return data[0];
    }

    gotoPrev(): void {
        if (this.additionalOffersPage() == 1) {
            this.switchViewType('trending');
            this.additionalOffersCount.set(0);
        } else {
            this.viewAllOffers = true;
            this.loadOffers(true, false);
        }
    }

    switchViewType(viewType: string): void {
        this.offerViewFilter.set(viewType);
        this.viewAllOffers = false;
        this.additionalOffersPage.set(1);
        this.additionalOffersCount.set(0);
        this.loadOffers(false, false);
    }

    changeSellerContractTermsVisibility(): void {
        this.showSellerContractTerms.set(!this.showSellerContractTerms());
        setTimeout(() => { this.changed = !this.changed; this.refreshView(); }, 1);
    }

    changeNSAISettings(): void {
        this.showNSAISettings.set(!this.showNSAISettings());
        setTimeout(() => { this.changed = !this.changed; this.refreshView(); }, 1);
    }

    changeDisclosureStatement(): void {
        this.showDisclosureStatement.set(!this.showDisclosureStatement());
        setTimeout(() => { this.changed = !this.changed; this.refreshView(); }, 1);
    }

    changeViewAccountsVisibility(): void {
        this.viewAccounts.set(!this.viewAccounts());
        setTimeout(() => { this.changed = !this.changed; this.refreshView(); }, 1);
    }

    statChanged(): void {
        setTimeout(() => { this.changed = !this.changed; this.refreshView(); }, 1000);
    }

    getColorBasedOnUtility(): string {
        if (this.account() == null) {
            return "";
        }
        if (this.account()!.isCommunity) {
            return environment.RI_COMMUNITY_COLOR;
        }
        return environment.RI_UTILITY_COLOR;
    }

    openConsumptionIfNotFilledDuringPageLoad(): void {
        if (this.hasFullConsumption) {
            return;
        }
        let alreadyOpened = this.generalService.getFromSession("consumption-opened-" + this.account()!.id);
        if (alreadyOpened == null) {
            this.generalService.storeInSession("consumption-opened-" + this.account()!.id, "true");
            this.openConsumptionDialog(true, false, this.account()!, null);
        }
    }

    openConsumptionIfConsumptionNotFilled(accountId: number, account: Account | null): void {
        var t = this;
        if (account == null) {
            this.generalService.getAccount(accountId).then(account => {
                this.generalService.storeInSession("consumption-opened-" + accountId, "true");
                this.openConsumptionDialog(true, false, account, () => { t.gotoStep(2); });
            });
        } else {
            this.generalService.storeInSession("consumption-opened-" + account.id, "true");
            this.openConsumptionDialog(true, false, account, () => { t.gotoStep(2); });
        }
    }

    afterLoadOffers(): void {
        this.benchmark.set("");
        this.generalService.setTopOffers(this.offers());
        const newOffersForChart = [];
        const newSavings = [];
        const newSavingsInUSD = [];
        if (this.user.isPreRegister) {
            this.isPreregisterMode.set(true);
        } else {
            this.isPreregisterMode.set(false);
        }
        if (this.offers().length == 0) {
            this.numberOffersLoad.set(0);
            if (this.offerViewFilter() != 'additional' && this.account() != null) {
                this.aiSubsctiptionMode.set(true);
                var t = this;
                setTimeout(() => { t.nooffers_slide_switch_1.set(false); }, 6000);
            }
            this.initOpenDivs();
            return;
        }
        this.numberOffersLoad.set(0);

        for (let i = 0; i < this.offers().length; i++) {
            this.numberOffersLoad.update(val => val + 1);
            let series: Serie[] = [];
            series.push(new Serie("Kilowant", (<SellOffer>this.offers()[i].offer).priceForBuyer()));
            if (this.showSavingsIn() == "USD") {
                if (!this.isBenchmarkCommunity()) {
                    series.push(new Serie("RI Energy", this.offers()[i].savings[DiffBenchmark.UTILITY].benchmarkValue!));
                } else {
                    series.push(new Serie("NextEra Energy", this.offers()[i].savings[DiffBenchmark.COMMUNITY].benchmarkValue!));
                }
            } else {
                if (!this.isBenchmarkCommunity()) {
                    series.push(new Serie("RI Energy", this.offers()[i].savings[DiffBenchmark.UTILITY].benchmarkValue!));
                } else {
                    series.push(new Serie("NextEra Energy", this.offers()[i].savings[DiffBenchmark.COMMUNITY].benchmarkValue!));
                }
            }
            let p = (<SellOffer>this.offers()[i].offer).termPeriod;

            var x = [];
            x.push(new RateData(p + " mths", series));
            newOffersForChart.push(x);
            newSavings.push((this.offers()[i].savings[this.diffBenchmark()] == null || this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmark == null || this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmark! < 0) ? 0 : this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmark!);
            newSavingsInUSD.push((this.offers()[i].savings[this.diffBenchmark()] == null || this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmarkInUSD == null || this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmarkInUSD! < 0) ? 0 : this.offers()[i].savings[this.diffBenchmark()].diffVsBenchmarkInUSD!);
        }
        this.offersForChart.set(newOffersForChart);
        this.savings.set(newSavings);
        this.savingsInUSD.set(newSavingsInUSD);

        this.savingsInUSDPresent = true;
        for (let i = 0; i < this.savingsInUSD().length; i++) {
            if (this.savingsInUSD()[i] <= 0) {
                this.savingsInUSDPresent = false;
            }
        }
        if (!this.savingsInUSDPresent) {
            this.showSavingsIn.set('percent');
        }
        if (this.max_savings() == '') {
            this.max_savings.set(Math.max(...this.savings()) + "%");
            this.max_savings_date.set(this.offers()[0].offer.termFrom);
        }

        for (let i = 0; i < this.offers().length; i++) {
            if (this.offers()[i].savings[this.diffBenchmark()] != null) {
                this.savingPresent = true;
            }
        }
        if (this.offers().length > 0 && this.isBenchmarkImmidiate(this.offers()[0])) {
            this.max_savings_rate = Math.floor((this.currentCost()[0].price! - this.offers()[0].offer.priceForBuyer()) / this.currentCost()[0].price! * 100);
        }
        var a = this.areas;
        var n = this.numberOffersLoad();
        setTimeout(() => {
            const newViewSmallChart = [];
            for (var i = 0; i < n; i++) {
                if (a.get(i)?.nativeElement.offsetHeight > 0) {
                    var size = [145, a.get(i)?.nativeElement.offsetHeight + 20];
                    newViewSmallChart.push(size);
                }
            }
            this.viewSmallChart.set(newViewSmallChart);
        }, 100);
        this.initOpenDivs();
    }

    gotoStep(num: number): void {
        let prevStep = this.step();
        this.step.set(num);
        setTimeout(() => { this.interactiveStep.set(num); this.refreshView(); }, 1);
        this.scrollNeeded = true;
        this.clearErrors();
        if (this.step() == 1) {
            this.cleanSelectedOffer();
            if (this.aiSubsctiptionMode() && this.hasOffersWithSavings()) {
                this.aiSubsctiptionMode.set(false);
            }
            this.viewOfferDetails.set(false);
            if (prevStep > 1) {
                this.loadOffers(false, false);
            }
            this.selectSortInitialised = false;
        }
        if (this.step() == 2) {
            if (this.user.isPreRegister) {
                if (this.user.company != null) {
                    this.companyName.set(this.user.company.name);
                }
            }
            if (!this.aiSubsctiptionMode()) {
                this.refreshAccountsForOfferSelection();
            } else {
                this.refreshAccountsForAISubscription();
            }
        }
        if (this.step() > 1 || (this.step() == 1 && this.viewOfferDetails())) {
            setTimeout(() => { this.scrollToCurrentStep(); }, 1000);
        }
        setTimeout(() => { this.refreshView(); }, 1000);
    }

    nextContractAutoAssignedLevel(num: number): void {
        if (this.nextContractLevel() == num) {
            return;
        }
        this.nextContractLevel.set(num);
    }

    loadAITask(aiTaskId: number): void {
        this.generalService.getAITask(aiTaskId)
            .then(aiTask => {
                this.activeAITask.set(aiTask);
            });
    }

    aiTaskUpdate(num: number): void {
        if (num == 0) {
            return;
        }
        if (this.activeAITask() != null && this.account() != null) {
            this.generalService.switchAITaskLevel([this.account()!.id], num).subscribe(() => {
                let confirmationText = "You are subscribed to kilowant Nonstop Shop ai Level " + num + ". You can modify or deactivate your subscription at any time by going to Main Menu > AI Tasks.";
                this.openResultsPopup("Nonstop Shop ai is Active", confirmationText, null);
                this.loadAITask(this.activeAITask()!.id);
                this.aiEditMode.set(false);
            });
        } else {
            this.cleanSelectedOffer();
            this.aiSubsctiptionMode.set(true);
            this.aiEditMode.set(false);
            this.nextContractLevel.set(num);
            this.gotoStep(2);
        }
    }

    scrollToCurrentStep(): void {
        if (this.scrollObservation) {
            this.scrollObservation.unsubscribe();
        }
        this.scrollObservation = timer(2000).subscribe(() => {
            let num = 0;
            if (this.scrollNeeded && this.step() == 1 && this.step1Element) {
                num = this.step1Element.nativeElement.getBoundingClientRect().top - this.generalService.getHeaderHeight() - 10;
            }
            if (this.scrollNeeded && this.step() == 2 && this.step2Element) {
                num = this.step2Element.nativeElement.getBoundingClientRect().top - this.generalService.getHeaderHeight() - 10;
            }
            if (this.scrollNeeded && this.step() == 3 && this.step3Element) {
                num = this.step3Element.nativeElement.getBoundingClientRect().top - this.generalService.getHeaderHeight() - 10;
            }
            this.generalService.scrollPageBy(num);
        });
    }

    offerInfoText(offer: OffersData, asterisk: boolean): string {
        if (offer.savings[this.diffBenchmark()] != null) {
            if (offer.savings[this.diffBenchmark()] != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD! > 0) {
                return "Save<br/> <b>$" + this.numberpipe.transform(this.positive(offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD), '', "en-US") + ((asterisk) ? "*" : "") + "</b>";
            } else if (offer.savings[this.diffBenchmark()] != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD! <= 0) {
                return "Lock Rate";
            }
            if (offer.savings[this.diffBenchmark()] != null && offer.savings[this.diffBenchmark()].diffVsBenchmark != null && offer.savings[this.diffBenchmark()].diffVsBenchmark! > 0) {
                return "Save<br/> <b class='b'>" + offer.savings[this.diffBenchmark()].diffVsBenchmark + "%" + ((asterisk) ? "*" : "") + "</b>";
            }
        }
        return "Lock Rate";
    }

    offerDiff(offer: OffersData): number {
        if (offer.savings[this.diffBenchmark()] != null) {
            if (offer.savings[this.diffBenchmark()].diffVsBenchmark! > 0) {
                return offer.savings[this.diffBenchmark()].diffVsBenchmark!;
            }
        }
        return 0;
    }

    getBenchmarkInfo(offer: OffersData): string {
        if (offer.savings[DiffBenchmark.UTILITY] != null) {
            return "*Savings vs. your alternative RI Energy supply rate.";
        }
        else if (offer.savings[DiffBenchmark.COMMUNITY] != null) {
            return "*Savings vs. your alternative community rate.";
        }
        return "Lock Rate";
    }

    addAccount(): void {
        this.router.navigate(["/account/add"]);
    }

    checkIfClientSelected(client: User | null): boolean {
        if (!this.user.isAdvisor) {
            return true;
        }
        if (client != null) {
            this.client.set(client);
        } else {
            this.client.set(this.generalService.getStoredClient());
        }
        this.switchAccountDelay = setTimeout(() => { this.account.set(null); }, 500);
        return !(this.client() == null);
    }

    loadSavings(): void {
        let userFilter = this.generalService.getStoredUserFilter();
        if (this.user.isAdvisor) {
            userFilter = null;
        }
        firstValueFrom(this.generalService.getUserSavings(userFilter)).then(data => this.userSavings.set(data));
    }

    selectOffer(pos: number | null, element: OffersData): void {
        this.selectedOffer.set(element);
        this.selectedOfferPrice.set(this.selectedOffer()!.offer.priceForBuyer());
        this.selectedOfferPosition = pos;
        this.viewOfferDetails.set(true);
        if (this.selectedOffer()!=null) {
           this.offerDetailsRestart.next(this.selectedOffer()!);
        }
        this.generalService.getSellOfferSellerContacts(element.offer.id).then((data: Pair) => {
            this.sellOfferContact.set(data.key);
        });
        if (this.user.isPreRegister) {
            this.isPreregisterMode.set(true);
        } else {
            this.isPreregisterMode.set(false);
        }
    }

    cleanSelectedOffer(): void {
        this.selectedOffer.set(null);
        this.sellOfferContact.set(null);
        this.selectedOfferPrice.set(0);
        this.selectedOfferPosition = null;
        this.viewOfferDetails.set(false);
    }

    isBenchmarkImmidiate(offer: OffersData): boolean {
        if (this.currentCost()[this.currentCost().length - 1] != null && this.currentCost()[this.currentCost().length - 1].price != null && (offer.offer.priceForBuyer() < this.currentCost()[this.currentCost().length - 1].price!)) {
            return true;
        }
        return false;
    }

    reset(): void {
        this.selectedOffer.set(null);
        this.sellOfferContact.set(null);
        this.selectedOfferPosition = null;
        this.loadOffers(false, false);
    }

    changeSelectedPrice(num: number): void {
        this.selectedOfferPrice.set(num);
    }

    changeDataset(diffBenchmark: DiffBenchmark): void {
    }

    isBenchmarkCommunity(): boolean {
        return this.diffBenchmark() == DiffBenchmark.COMMUNITY;
    }

    slideToOffer(): void {
        this.generalService.scrollPageTop(0);
    }

    getPeriod(offer: any): number {
        if (offer == null) {
            return 0;
        }
        return offer.termPeriod;
    }

    gotoOfferByPos(pos: number): void {
        this.selectOffer(0, this.offers()[pos]);
        this.cancelDrawerTimer();
        this.cancelSelectedPricePlanTimer();
        this.aiEditMode.set(false);
        this.scrollNeeded = true;
        this.scrollToCurrentStep();
    }

    openBottomSlide(): void {
        if (this.numberOffersLoad() == -1) {
            return; // bottom area shall not be opened until offers are loaded
        }
        if (this.numberOffersLoad() > 0 && (this.getActiveAITaskID() != null) && ((this.getPriceStatsText(this.offers()[0]) != "high") || this.ifNewOffer(this.offers()[0]))) {
            return;
        }
        this.aiEditMode.set(true);
        this.cancelDrawerTimer();
    }

    getLastOfferNumber(): number {
        return Math.min(this.additionalOffersPage() * 3, this.additionalOffersCount());
    }

    ifPriceStatsAreActive(offer: OffersData): boolean {
        if (offer.priceStats == null) {
            return false;
        }
        if (offer.priceStats.min == offer.priceStats.max) {
            return false;
        }
        if (offer.priceStats.length < 2) {
            return false;
        }
        return true;
    }

    ifNewOffer(offer: OffersData): boolean {
        return (offer.priceStats == null) || (offer.priceStats.length < 2);
    }

    getPriceStatsText(offer: OffersData): string {
        if (offer.priceStats != null) {
            var abs_min_price = offer.priceStats!.min;
            var abs_max_price = offer.priceStats!.max;
            if (offer.offer.priceForBuyer() < abs_min_price) {
                abs_min_price = offer.offer.priceForBuyer();
            }
            if (offer.offer.priceForBuyer() > abs_max_price) {
                abs_max_price = offer.offer.priceForBuyer();
            }
            var diff = abs_max_price - abs_min_price;

            var min_price = abs_min_price + diff / 3;
            var max_price = abs_max_price - diff / 3;
            if (offer.offer.priceForBuyer() <= min_price) {
                return "low";
            }
            if (offer.offer.priceForBuyer() >= max_price) {
                return "high";
            }
            return "typical";
        }
        return "";
    }

    getPriceStatsPercent(offer: OffersData): number {
        if (this.ifPriceStatsAreActive(offer) && offer.priceStats != null) {
            var abs_min_price = offer.priceStats.min;
            var abs_max_price = offer.priceStats.max;
            if (offer.offer.priceForBuyer() < abs_min_price) {
                abs_min_price = offer.offer.priceForBuyer();
            }
            if (offer.offer.priceForBuyer() > abs_max_price) {
                abs_max_price = offer.offer.priceForBuyer();
            }
            var diff = abs_max_price - abs_min_price;

            if (diff == 0) {
                return 50;
            }
            return Math.round((offer.offer.priceForBuyer() - abs_min_price) * 100 / diff);
        }
        return 50;
    }

    getPriceStatsSavingsText(savingsInfo: SavingsInfo, negativeValues: boolean): string {
        if (savingsInfo != null) {
            if (savingsInfo != null && savingsInfo.diffVsBenchmarkInUSD != null && savingsInfo.diffVsBenchmarkInUSD! > 0) {
                return "Save $" + this.numberpipe.transform(this.positive(savingsInfo.diffVsBenchmarkInUSD), "", "en-US") + " (" + savingsInfo.diffVsBenchmark + "%)";
            } else if (savingsInfo != null && savingsInfo.diffVsBenchmarkInUSD != null && savingsInfo.diffVsBenchmarkInUSD! <= 0) {
                if (negativeValues) {
                    return "Lose $" + this.numberpipe.transform(this.positive(savingsInfo.diffVsBenchmarkInUSD), "", "en-US") + " (" + savingsInfo.diffVsBenchmark + "%)";
                }
                return "Lock Rate";
            }
            if (savingsInfo != null && savingsInfo.diffVsBenchmark != null && savingsInfo.diffVsBenchmark! > 0) {
                return "Save " + savingsInfo.diffVsBenchmark + "%";
            } else if (savingsInfo.diffVsBenchmark! <= 0) {
                return savingsInfo.diffVsBenchmark + "%";
            }
        }
        if (negativeValues) {
            return "";
        }
        return "Lock Rate";
    }

    showViewInUSD(offer: OffersData): boolean {
        if (offer.savings[this.diffBenchmark()] != null) {
            if (offer.savings[this.diffBenchmark()] != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD! > 0) {
                return false;
            } else if (offer.savings[this.diffBenchmark()] != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD != null && offer.savings[this.diffBenchmark()].diffVsBenchmarkInUSD! < 0) {
                return false;
            }
            return true;
        }
        return false;
    }

    isConsumptionFull(consumptionData: ConsumptionDataType | null): boolean {
        return (consumptionData == ConsumptionDataType.FULL);
    }

    isConsumptionEstimated(consumptionData: ConsumptionDataType | null): boolean {
        return (consumptionData == ConsumptionDataType.ESTIMATED);
    }

    updateAccountConsumption(): void {
        this.callUpdate.next(true);
    }

    refreshAccountsForOfferSelection(): void {
        this.generalService.getSellOfferPerAccount(this.account(), this.selectedOffer()?.offer.id!).then((ele) => {
            let relatedContract = ele.value;
            if (relatedContract != null) {
                this.router.navigate(["/view-priceplan/" + relatedContract.id]);
                return;
            }
            this.generalService.getAccountsNonUsedPerPeriod(<SellOffer>this.selectedOffer()?.offer!, null, null, this.account()!).then((accountsSelector) => {
                this.accountsSelector = accountsSelector;
                this.accountsDataConfirmNeeded.set(false);
                this.selectedAccountsIds = [];
                this.selectedAccounts = [];
                this.addRemoveAccount(this.account()!.id, true);
                this.changeCurrentAccountStep(1);
                this.refreshView();
            });
        });
    }

    refreshAccountsForAISubscription(): void {
        this.generalService.getAccountsNonUsedInAITask().then(
            (accountsSelector) => {
                this.accountsSelector = accountsSelector;
                this.accountsDataConfirmNeeded.set(false);
                this.selectedAccountsIds = [];
                this.selectedAccounts = [];
                this.addRemoveAccount(this.account()!.id, true);
                this.changeCurrentAccountStep(1);
                this.refreshView();
            });
    }

    ifItemSelected(accountId: number): boolean {
        let pos = this.selectedAccountsIds.indexOf(accountId);
        return (pos >= 0);
    }

    selectItem(accountId: number): void {
        if (this.ifItemSelected(accountId)) {
            this.addRemoveAccount(accountId, false);
        } else if (this.ifItemSelectable(accountId)) {
            this.addRemoveAccount(accountId, true);
        }
        this.changeCurrentAccountStep(1);
        this.refreshView();
    }

    changeCurrentAccountStep(newStep: number): void {
        if (this.selectedAccounts[newStep - 1] == null) {
            this.generalService.getAccount(this.selectedAccountsIds[newStep - 1]).then(account => {
                this.selectedAccounts[newStep - 1] = account;
                this.currentAccountStep.set(newStep);
            });
        } else {
            this.currentAccountStep.set(newStep);
        }
    }

    ifItemSelectable(accountId: number): boolean {
        if (this.ifItemSelected(accountId)) {
            return true;
        }
        if (this.selectedAccounts.length == 0) {
            return true;
        }
        let selectedGroup = this.getSelectedGroupPosition();
        if (selectedGroup == -1) {
            return true;
        }
        for (let j = 0; j < this.accountsSelector[selectedGroup].accountData.length; j++) {
            if (this.accountsSelector[selectedGroup].accountData[j].id == accountId) {
                return true;
            }
        }
        return false;
    }

    selectAll(): void {
        this.selectAllChecked.set(!this.selectAllChecked());
        for (let i = 0; i < this.accountsSelector.length; i++) {
            for (let j = 0; j < this.accountsSelector[i].accountData.length; j++) {
                if (this.accountsSelector[i].accountData[j].id != this.account()!.id &&
                    (!(this.aiSubsctiptionMode() && this.nextContractLevel() > 1 && !this.accountsSelector[i].accountData[j].hasFullConsumption)) &&
                    ((this.accountsSelector[i].accountData[j].savings == null) || (this.accountsSelector[i].accountData[j].savings != null && this.accountsSelector[i].accountData[j].savings!.diffVsBenchmark != null && this.accountsSelector[i].accountData[j].savings!.diffVsBenchmark! >= 0))) {
                    this.addRemoveAccount(this.accountsSelector[i].accountData[j].id, this.selectAllChecked());
                }
            }
        }
        if (!this.selectAllChecked()) {
            this.changeCurrentAccountStep(1);
        }
        this.refreshView();
    }

    addRemoveAccount(accountId: number, adding: boolean): void {
        let pos = this.selectedAccountsIds.indexOf(accountId);
        if (adding && pos == -1) {
            let accountData = this.getAccountDataFromSelector(accountId);
            this.selectedAccountsIds.push(accountId);
            this.selectedAccounts.push((accountData != null) ? accountData.account : null);
            this.selectedAccountsChanged.push(false);
        }
        if (!adding && pos >= 0) {
            this.selectedAccountsIds.splice(pos, 1);
            this.selectedAccounts.splice(pos, 1);
            this.selectedAccountsChanged.splice(pos, 1);
        }
        if (this.selectedAccounts.length > 0) {
            this.clearErrors();
        }
        this.refreshView();
    }

    getAccountDataFromSelector(accountId: number): AccountData | null {
        for (let i = 0; i < this.accountsSelector.length; i++) {
            for (let j = 0; j < this.accountsSelector[i].accountData.length; j++) {
                if (this.accountsSelector[i].accountData[j].id == accountId) {
                    return this.accountsSelector[i].accountData[j];
                }
            }
        }
        return null;
    }

    getSelectedGroupPosition(): number {
        for (let i = 0; i < this.accountsSelector.length; i++) {
            for (let j = 0; j < this.accountsSelector[i].accountData.length; j++) {
                if (this.ifItemSelected(this.accountsSelector[i].accountData[j].id)) {
                    return i;
                }
            }
        }
        return -1;
    }

    updateAccount(): void {
        this.callUpdate.next(true);
    }

    confirmLoginDetails(): void {
        this.clearErrors();
        if (this.user.email == null || this.user.email.length == 0) {
            this.addError("Email");
        }
        if (this.password() == null || this.password().length == 0) {
            this.addError("Password");
        }
        if (this.password2() == null || this.password2().length == 0) {
            this.addError("Password2");
        }
        if (this.user.company != null && (this.companyName() == null || this.companyName()!.length == 0)) {
            this.addError("Company");
        }
        if (this.user.zip == null || this.user.zip.length == 0) {
            this.addError("zip");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to register.");
            this.analyticsService.trackEvent("form_error", { form: "finish_pre_register", error_key: "missing_fields" });
            return;
        }
        if (this.user.city == null || this.user.city.length == 0 || this.user.state == null) {
            this.addError("zip");
            this.setErrorText("Zip code is not correct.");
            return;
        }
        if (this.password() != this.password2()) {
            this.setErrorText("Passwords do not match.");
            this.addError("Password");
            this.addError("Password2");
            this.analyticsService.trackEvent("form_error", { form: "finish_pre_register", error_key: "password_mismatch" });
            return;
        }

        if (this.user.company != null && this.companyName() != null) {
            this.user.company.name = this.companyName()!;
        } else if (this.companyName() != null && this.companyName()!.length > 0) {
            this.user.company = new Company();
            this.user.company.name = this.companyName()!;
        }

        this.generalService.checkRegisterUserEmail(this.user.email).subscribe(
            {
                next: (response: any) => {
                    this.dialogRef = this.dialog.open(ConfirmationDialog, {
                        disableClose: false
                        , panelClass: "i-icon-popup"
                    });
                    this.dialogRef.componentInstance.confirmTitle = "Please Confirm Your Email";
                    this.dialogRef.componentInstance.confirmMessage = this.user.email;
                    this.dialogRef.componentInstance.cancelButton = "Change";
                    this.dialogRef.afterClosed().subscribe((result: any) => {
                        if (result) {
                            this.proceedFinishPreRegister();
                        }
                        this.dialogRef = null!;
                    });
                },
                error: (err: any) => {
                    if (err.status == 400) {
                        this.addError("email");
                        this.setErrorText(err.error);
                        this.analyticsService.trackEvent("form_error", { form: "finish_pre_register", error_key: "email_rejected" });
                    }
                }
            }
        );
    }

    proceedFinishPreRegister(): void {
        this.generalService.finishPreRegister(this.user, this.password()).subscribe({
            next: () => {
                this.generalService.clearUserCache();
                this.reloadUser().then(u => {
                    this.generalService.changeIsPreRegister(u);
                    this.gotoStep(3);
                });
            },
            error: (err: any) => {
                this.setErrorText(err.error);
                this.analyticsService.trackEvent("form_error", { form: "finish_pre_register", error_key: "finish_rejected" });
                return;
            }
        });
    }

    editFinish(): void {
        if (this.selectedAccounts.length == 0) {
            return;
        }
        this.selectedAccountsChanged[this.currentAccountStep() - 1] = false;
        if (this.currentAccountStep() < this.selectedAccounts.length) {
            this.changeCurrentAccountStep(this.currentAccountStep() + 1);
            return;
        } else {
            this.editFinishAfter();
        }
    }

    autoAcceptAll(): void {
        for (let i = 0; i < this.selectedAccountsChanged.length; i++) {
            this.selectedAccountsChanged[i] = false;
        }
        this.editFinishAfter();
    }

    editFinishAfter(): void {
        this.clearErrors();
        this.gotoStep(3);
    }

    changeStep(p: number): void {
        if (this.selectedAccountsChanged[this.currentAccountStep() - 1]) {
            return;
        }
        this.changeCurrentAccountStep(p);
        this.clearErrors();
    }

    changeCurrentAccountInfo(): void {
        if (this.selectedAccountsChanged[this.currentAccountStep() - 1]) {
            return;
        }
        this.selectedAccountsChanged[this.currentAccountStep() - 1] = true;
        this.refreshView();
    }

    formatSelectedAccounts(): Pair[] {
        let res: Pair[] = [];
        res.push(new Pair(this.account()!.utility!.name + " > " + this.account()!.loadZone!.name + " > " + this.account()!.rateClass!.name, []));
        for (var z = 0; z < this.selectedAccountsIds.length; z++) {
            res[0].value.push(this.getAccountDataFromSelector(this.selectedAccountsIds[z]));
        }
        return res;
    }

    sendTerms(): void {
        document.location.href = "/pdf/" + (<SellOffer>this.selectedOffer()!.offer).sellerContract.termsDocument.value;
    }

    acceptSellOffer(): void {
        if (this.selectedAccounts.length == 0) {
            this.setErrorText("You need to select at least one account");
            return;
        }
        this.acceptProcess();
    }

    acceptProcess(): void {
        this.loaderService.setDisabled(false);        
        this.generalService.acceptSellOffer(<SellOffer>this.selectedOffer()!.offer, this.selectedAccountsIds, this.aiSubscription()).subscribe((newContractId) => {
            if (newContractId != null && newContractId > 0) {
                this.autoOpenContractId = newContractId;
                this.openResultsPopup("Your Price Plan is Pending", "A confirmation email has been sent to " + this.user.email + " including the details of your accepted price plan. ", null);
                return;
            }
        });
    }

    getRatingText(basicText: string, utilityDate: string | null, communityDate: string | null): string {
        let text = basicText + "<br/><br/>";
        let p = 1;
        let asterikVals: string[] = [];
        asterikVals.push("RI Energy");
        asterikVals.push("NextEra Energy");

        if (utilityDate != null && utilityDate.length > 0 && asterikVals.includes("RI Energy")) {
            text += this.repeater("*", p++) + " RI Energy Last Resort Service rate beyond " + utilityDate + " is estimated and subject to adjustment<br/>";
        }
        if (communityDate != null && communityDate.length > 0 && asterikVals.includes("NextEra Energy")) {
            text += this.repeater("*", p++) + " NextEra Energy rate beyond " + communityDate + " is estimated and subject to adjustment<br/>";
        }
        return text;
    }

    getPagedRatesBasedText(isRate: boolean, utilityDate: string | null, communityDate: string | null, consumptionData: ConsumptionDataType | null): string {
        if (isRate) {
            return "<h2>How We Calculate Cost Reduction</h2>Reduction percentage based upon the difference between the lowest kilowant #1 Trending Offer rate and the billed supply rate in effect at the time for your selected price plan.";
        }

        let text = "<h2>How is Savings Calculated?</h2>Savings based upon  " + ((consumptionData == null || consumptionData == ConsumptionDataType.NO_DATA) ? "simple average" : "the usage weighted average") + " difference between the offer rate and utility rate for the same term (apples to apples comparison).<br/><br/>";
        let p = 1;
        let asterikVals: string[] = [];

        if (this.pagedRates.length > 0) {
            for (let x = 0; x < this.pagedRates.length; x++) {
                for (let i = 0; i < this.pagedRates[x].series.length; i++) {
                    let testVal = this.pagedRates[x].series[i].name.replace(/\s*\(Projected\)\s*/, "");
                    if (testVal == "RI Energy") {
                        if (!asterikVals.includes(testVal)) {
                            asterikVals.push(testVal);
                        }
                    }
                    if (testVal == "NextEra Energy") {
                        if (!asterikVals.includes(testVal)) {
                            asterikVals.push(testVal);
                        }
                    }
                }
            }
        }

        if (utilityDate != null && utilityDate.length > 0 && asterikVals.includes("RI Energy")) {
            text += this.repeater("*", p++) + " RI Energy Last Resort Service rate beyond " + utilityDate + " is estimated and subject to adjustment<br/>";
        }
        if (communityDate != null && communityDate.length > 0 && asterikVals.includes("NextEra Energy")) {
            text += this.repeater("*", p++) + " NextEra Energy rate beyond " + utilityDate + " is estimated and subject to adjustment<br/>";
        }
        return text;
    }

    repeater(chr: string, num: number) {
        let outStr = "";
        for (let i = 0; i < num; i++) {
            outStr = outStr + chr;
        }
        return outStr;
    }

    getDiffPeriodicityWise(fromDate: DateTime, toDate: DateTime, periodicity: string): number {
        if (periodicity == null || periodicity == "" || periodicity == "monthly") {
            return Math.floor(fromDate.diff(toDate, ["months"])["months"])
        }
        if (periodicity == "quarterly") {
            return Math.floor(fromDate.diff(toDate, ["months"])["months"] / 3);
        }
        if (periodicity == "yearly") {
            return Math.floor(fromDate.diff(toDate, ["years"])["years"]);
        }
        return 0;
    }

    estimateSavings(): void {
        this.paying_diff = 0;
        this.diff_start = null;
        let total_avg_kilowant = 0;
        let total_avg_utility = 0;
        this.future_price = 0;
        this.future_months = 0;
        for (let i = 0; i < this.rates.length; i++) {
            let arr = this.rates[i].name.split("/");
            var month = parseInt(arr[0]);
            var year = parseInt(arr[1]);
            if (DateTime.local(year, month, 1) > this.currentDate) {
                this.future_months += 1;
                for (let x = 0; x < this.rates[i].series.length; x++) {
                    if (this.rates[i].series[x].name != "Kilowant") {
                        this.future_price += this.rates[i].series[x].value;
                    }
                }
            }

            if (this.rates[i].series.length > 1) {
                let kilowant_arr: number[] = [];
                let utility_arr: number[] = [];
                for (let x = 0; x < this.rates[i].series.length; x++) {
                    if (DateTime.local(year, month, 1) < this.currentDate) {
                        if (this.rates[i].series[x].name == "Kilowant") {
                            if (this.diff_start == null) {
                                this.diff_start = DateTime.local(year, month, 1).monthShort + " " + year;
                            }
                            kilowant_arr.push(this.rates[i].series[x].value);
                        } else {
                            utility_arr.push(this.rates[i].series[x].value);
                        }
                    }
                }
                if (this.estimateAvg(kilowant_arr) > 0 && this.estimateAvg(utility_arr) > 0) {
                    total_avg_kilowant += this.estimateAvg(kilowant_arr);
                    total_avg_utility += this.estimateAvg(utility_arr);
                }
            }
        }
        if (this.future_months > 0) {
            this.future_price = this.future_price / this.future_months;
        }
        if (total_avg_utility > 0) {
            this.paying_diff = Math.round((total_avg_utility - total_avg_kilowant) / total_avg_utility * 100);
        }
    }

    changeCurrentElectricityPlan(): void {
        this.account()!.startingContractConfirmed = false;
        this.generalService.updateAccount(this.account()!, [], [], null).subscribe(() => {
            this.generalService.clearUserCompaniesFromSession();
            this.router.navigate(["/user/home"]);
        },
            err => {
                if (err.status == 400) {
                    this.setErrorText(err.error);
                }
            });
    }

    generateAITask(): void {
        var existingAITask = 0;
        if (this.currentCost()[this.selectedOffersPeriod()].aiTaskId != null && this.currentCost()[this.selectedOffersPeriod()].aiTaskId! > 0) {
            existingAITask = this.currentCost()[this.selectedOffersPeriod()].aiTaskId!;
        }
        this.loaderService.setDisabled(false);

        if (existingAITask > 0 && this.account() != null) {
            this.generalService.getAITask(existingAITask)
                .then(aiTask => {
                    var promises = [];
                    if (aiTask.level != this.nextContractLevel()) {
                        promises.push(this.generalService.switchAITaskLevel([this.account()!.id], this.nextContractLevel()));
                    }
                    if (promises.length > 0) {
                        zip(promises).subscribe(() => {
                            this.goto("ai-task/details/" + existingAITask);
                            return;
                        });
                    }
                });
        } else {
            var termTo = null;
            if (this.currentCost()[this.selectedOffersPeriod()].termTo != null) {
                termTo = this.applySitesTimeZone(
                    DateTime.fromJSDate(this.currentCost()[this.selectedOffersPeriod()].termTo!).startOf("day")
                );
            }
            var t = this;
            this.generalService.addAITasks(this.selectedAccountsIds, this.nextContractLevel(),
                (this.user.isPreRegister) ? this.user : null, this.password()).subscribe(() => {
                    this.aiEditMode.set(false);
                    this.openResultsPopup("Nonstop Shop ai is Active", "You are subscribed to kilowant Nonstop Shop ai Level " + this.nextContractLevel() + ".  You can modify or deactivate your subscription at any time by going to Main Menu > AI Tasks.", null);
                    t.changeAccount(t.account());
                    this.loaderService.setDisabled(true);
                    return;
                });
        }
    }

    getPricingStartDate(inDate: Date | null): Date {
        if (inDate == null) {
            return this.currentDate.startOf("month").startOf("day").plus({ month: 1 }).toJSDate();
        }
        var dt = this.applySitesTimeZone(DateTime.fromJSDate(inDate).startOf("month").startOf("day"));
        var currentTs = this.currentDate.startOf("month").startOf("day");
        if (dt <= currentTs) {
            return dt.plus({ month: 1 }).toJSDate();
        }
        return inDate;
    }

    addContract(): void {
        this.router.navigate(["/priceplans/details"]);
    }

    confirmStartingContract(): void {
        if (!this.selectModeChanged()) {
            return;
        }
        if (this.setStartingContract() != -1 && this.setStartingContract() != 1 && this.setStartingContract() != 0) {
            return;
        }
        if (this.setStartingContract() == -1) {
            this.addContract();
        } else {
            this.account()!.startingContractConfirmed = true;
            this.account()!.isCommunity = (this.setStartingContract() == 1);
            this.generalService.updateAccount(this.account()!, [], [], null).subscribe(() => {
                this.generalService.clearUserCompaniesFromSession();
                // Persist the updated account (incl. isCommunity) to the session snapshot so a
                // page refresh reads the current value instead of the stale stored copy.
                sessionStorage.setItem("selected_account", JSON.stringify(this.account()!));
                this.changeAccount(this.account());
            },
                err => {
                    if (err.status == 400) {
                        this.setErrorText(err.error);
                    }
                });
        }
    }

    getStateName(state: State | null): string {
        if (state == null) {
            return "";
        }
        return state.stateCode;
    }

    zipChanged(): void {
        this.resetError('zip');
        this.user.city = "";
        this.user.state = null;
        if (this.user.zip != null && this.user.zip?.length > 2) {
            let request = this.generalService.getStateCityByZip(this.user.zip);
            if (request == null) {
                return;
            }
            firstValueFrom(request).then(data => {
                if (data != null) {
                    this.user.state = data.key;
                    this.user.city = data.value;
                    this.refreshView();
                }
            });
        }
    }

    refreshContractSliderPositions(): void {
        var startPos = Math.max(0, Math.round(this.sliderCurrentPosition() - this.sliderMaxElements / 2));
        var endPos = Math.min(startPos + this.sliderMaxElements - 1, this.currentCost().length - 1);
        if (endPos == this.currentCost().length - 1) {
            startPos = Math.max(0, endPos - this.sliderMaxElements + 1);
        }
        const newPositions = [];
        for (var i = startPos; i <= endPos; i++) {
            newPositions.push(i);
        }
        this.sliderPositions.set(newPositions);
    }

    contractSliderChanged(data: SlidesOutputData): void {
        this.sliderCurrentPosition.set(Number(data.startPosition));
        this.refreshContractSliderPositions();
    }

    contractSliderChangeSliderPosition(i: number): void {
        this.contractsSlider.to(i.toString());
    }

    contractSliderTextForPosition(i: number): string {
        var outText = '';
        if (i == 0) {
            outText = 'Current';
        } else {
            outText = 'Next';
        }
        if (this.sliderCurrentPosition() == i) {
            outText += " Price Plan"
        }
        return outText;
    }

    showPassword(num: number) {
        if (num == 1) {
            if (this.passwordType() === "password") {
                this.passwordType.set("text");
            } else {
                this.passwordType.set("password");
            }
        } else {
            if (this.passwordType2() === "password") {
                this.passwordType2.set("text");
            } else {
                this.passwordType2.set("password");
            }
        }
    }

    getName(account: Account): string {
        return Account.accountIdent(account);
    }

    getUser(): User {
        if (this.user.isAdvisor && this.client() != null) {
            return this.client()!;
        }
        return this.user;
    }

    getBottomHeight(): string {
        if (!this.aiEditMode()) {
            return 'height:70px';
        }
        if (this.activeAITask() && this.numberOffersLoad() == 0) {
            return 'height: 400px;';
        }
        if (!this.activeAITask() && !this.howItWorksOpened() && this.numberOffersLoad() == 0) {
            return 'height: 450px;';
        }
        if (!this.activeAITask() && !this.howItWorksOpened() && this.numberOffersLoad() > 0) {
            return 'height: 400px;';
        }
        if (!this.activeAITask() && this.howItWorksOpened() && this.numberOffersLoad() == 0) {
            return 'height: 690px;';
        }
        if (!this.activeAITask() && this.howItWorksOpened() && this.numberOffersLoad() > 0) {
            return 'height: 690px;';
        }

        if (!this.activeAITask() && !this.howPricePlansQualifyOpened()) {
            return 'height: 670px;';
        }
        if (!this.howPricePlansQualifyOpened()) {
            return 'height: 610px;';
        }
        return 'height: 690px;';
    }

    hasConsumption(): boolean {
        if (!this.currentCost() || !this.currentCost()[this.selectedOffersPeriod()]) {
            return false;
        }
        let consubmtionDataType = this.currentCost()[this.selectedOffersPeriod()].consumptionData;
        if (consubmtionDataType == null) {
            return false;
        }
        return consubmtionDataType === ConsumptionDataType.FULL;
    }

    getSquizedView(view: [number, number]): [number, number] {
        return [view[0] - 15, view[1]];
    }

}
